import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HiArrowRight, HiArrowLeft } from "react-icons/hi";
import { MdCalendarToday } from "react-icons/md";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateItems } from "../../hooks/useTranslate";

// ─── Teks UI statis ────────────────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    // Hero
    heroLabel:   "Consulting",
    heroTitle:   "Towards social impact that is sustainable and measurable",

    // ContentBlock
    servicesLabel:   "Our Services",
    servicesTitle:   "What We Offer",
    servicesDesc1:   "If you are a social or non-profit organization looking to increase your next investment round or diversify your funding sources, Rumoh Pangan Aceh is your trusted partner.",
    servicesDesc2:   "We also have an extensive range of services covering assistance for organizations seeking to integrate ESG principles — from workshops and sustainability strategies to certification.",

    // Services cards (hardcoded, diterjemahkan via UI_TEXT)
    services: [
      {
        num: "01",
        title: "Fundraising Assistance",
        desc: "Rumoh Pangan Aceh helps food organizations grow by reviewing business plans, developing financial projections, calculating valuations, and guiding fundraising and investment processes.",
      },
      {
        num: "02",
        title: "Funding Diversification",
        desc: "We help civil society and non-profit organizations explore potential revenue streams. Through social entrepreneurship approaches, we help them become self-sustaining organizations.",
      },
      {
        num: "03",
        title: "Program Management",
        desc: "Rumoh Pangan Aceh assists in field research, target farmer mapping, program due diligence, and building sustainable food distribution networks across Aceh districts.",
      },
      {
        num: "04",
        title: "ESG Compliance",
        desc: "We provide guidance for organizations seeking to integrate ESG principles into their operations, from workshops and sustainability strategies to impact reports and certification.",
      },
    ],

    // CaseStudies
    caseLabel:   "Portfolio",
    caseTitle:   "Case Studies",
    viewAll:     "View All",
    learnMore:   "Learn More",
  },

  ID: {
    heroLabel:   "Consulting",
    heroTitle:   "Menuju dampak sosial yang berkelanjutan dan terukur",

    servicesLabel:   "Layanan Kami",
    servicesTitle:   "Apa yang Kami Tawarkan",
    servicesDesc1:   "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Jika Anda adalah organisasi sosial atau nirlaba yang ingin meningkatkan putaran investasi berikutnya atau mendiversifikasi sumber pendanaan, maka Rumoh Pangan Aceh adalah mitra terpercaya Anda.",
    servicesDesc2:   "Kami juga memiliki rangkaian layanan ekstensif yang mencakup bantuan bagi organisasi yang berupaya mengintegrasikan prinsip ESG. Mulai dari workshop, strategi keberlanjutan, hingga sertifikasi.",

    services: [
      {
        num: "01",
        title: "Bantuan Penggalangan Dana",
        desc: "Rumoh Pangan Aceh membantu organisasi pangan berkembang dengan mengkaji rencana bisnis, mengembangkan proyeksi keuangan, menghitung valuasi, dan mendampingi proses penggalangan dana serta investasi.",
      },
      {
        num: "02",
        title: "Diversifikasi Pendanaan",
        desc: "Kami membantu masyarakat sipil dan organisasi nirlaba mengeksplorasi aliran pendapatan potensial. Melalui pendekatan kewirausahaan sosial, kami membantu mereka menjadi organisasi mandiri.",
      },
      {
        num: "03",
        title: "Manajemen Program",
        desc: "Rumoh Pangan Aceh membantu dalam riset lapangan, pemetaan petani sasaran, due diligence program, dan pembangunan jaringan distribusi pangan berkelanjutan di seluruh kabupaten Aceh.",
      },
      {
        num: "04",
        title: "Kepatuhan ESG",
        desc: "Kami memberikan pendampingan bagi organisasi yang berupaya mengintegrasikan prinsip ESG dalam operasionalnya, mulai dari workshop, strategi keberlanjutan, laporan dampak, hingga sertifikasi.",
      },
    ],

    caseLabel:   "Portofolio",
    caseTitle:   "Studi Kasus",
    viewAll:     "Lihat Semua",
    learnMore:   "Pelajari Lebih",
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const formatTanggal = (dateStr, language) =>
  new Date(dateStr).toLocaleDateString(language === "EN" ? "en-US" : "id-ID", {
    day: "numeric", month: "long", year: "numeric",
  });

const truncateText = (text, max = 100) => {
  if (!text) return "-";
  const plain = text.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return plain.length <= max ? plain : plain.substring(0, max) + "…";
};

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.12) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Hero ──────────────────────────────────────────────────────────────────────
const Hero = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full overflow-hidden">
      <div
        className="absolute left-0 top-0 h-full w-1 bg-[#c9862a] z-20"
        style={{ transform: loaded ? "scaleY(1)" : "scaleY(0)", transformOrigin: "top", transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s" }}
      />

      <div className="grid grid-cols-4 grid-rows-2 h-[340px] md:h-[420px]">
        {[
          "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?w=600&q=75",
          "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=600&q=75",
          "https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&q=75",
          "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=600&q=75",
          "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&q=75",
          "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=75",
          "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=75",
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=75",
        ].map((src, i) => (
          <div key={i} className="overflow-hidden">
            <img src={src} alt="" className="w-full h-full object-cover brightness-[0.6] hover:brightness-75 transition-all duration-700" />
          </div>
        ))}
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0c0a]/80 via-[#1a1a18]/55 to-transparent" />

      <div className="absolute inset-0 flex flex-col justify-end pb-14 px-8 md:px-16 lg:px-24">
        <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(24px)", transition: "all 0.9s ease 0.3s" }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#c9862a]" />
            <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
              {tx.heroLabel}
            </span>
          </div>
          {/* Judul hero — ganti instan dari UI_TEXT */}
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-[1.0] max-w-4xl">
            {tx.heroTitle}
          </h1>
        </div>
      </div>
    </section>
  );
};

// ─── Content + Services ────────────────────────────────────────────────────────
const ContentBlock = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const ref = useRef(null);
  const inView = useInView(ref);

  // Services dari UI_TEXT — langsung ganti instan, tanpa API call
  const services = tx.services;

  return (
    <section ref={ref} className="bg-white py-6 px-4 md:px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-[#1a4a35] px-8 md:px-16 lg:px-20 py-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 font-serif text-[200px] font-bold leading-none select-none pointer-events-none opacity-[0.04] text-white">
            RPA
          </div>

          {/* Header */}
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6"
            style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(24px)", transition: "all 0.8s ease" }}
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-10 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                  {tx.servicesLabel}
                </span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
                {tx.servicesTitle}
              </h2>
            </div>
            <div className="max-w-md">
              <p className="text-white/80 text-base leading-relaxed mb-4">{tx.servicesDesc1}</p>
              <p className="text-white/60 text-sm leading-relaxed">{tx.servicesDesc2}</p>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-14">
            <div className="flex-1 h-px bg-white/10" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Services grid — ganti instan dari UI_TEXT, tanpa MyMemory */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map(({ num, title, desc }, i) => (
              <div
                key={num}
                className="group border border-white/10 p-7 hover:border-[#c9862a]/40 transition-all duration-300 relative"
                style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(24px)", transition: `all 0.7s ease ${0.2 + i * 0.1}s` }}
              >
                <div className="absolute top-4 right-5 font-serif text-6xl font-bold text-white/5 leading-none select-none group-hover:text-[#c9862a]/10 transition-colors duration-300">
                  {num}
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[#c9862a] text-xl font-bold">✳</span>
                  <h3 className="text-[#c9862a] font-bold text-base tracking-wide">{title}</h3>
                </div>
                <div className="w-8 h-[2px] bg-[#c9862a]/40 mb-4 ml-8 group-hover:w-16 transition-all duration-500" />
                <p className="text-white/75 text-sm leading-relaxed pl-8">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Case Studies ──────────────────────────────────────────────────────────────
const CaseStudies = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const ref = useRef(null);
  const inView = useInView(ref);
  const navigate = useNavigate();

  const [rawStories, setRawStories] = useState([]);
  const [current, setCurrent] = useState(0);
  const visibleCount = 3;

  // 1. Fetch data mentah dari API (hanya sekali)
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const apiUrl = process.env.REACT_APP_URL_API;
        const res = await axios.get(`${apiUrl}/all-stories-exclude-post`);
        const data = Array.isArray(res.data) ? res.data : [];
        setRawStories(data.slice(0, 6));
      } catch (err) {
        console.error("Error fetching Stories:", err);
      }
    };
    fetchStories();
  }, []);

  // 2. Terjemahkan field "judul" dan "deskripsi" via MyMemory
  //    Hanya aktif saat language === "EN", hasil di-cache per sesi
  const { translated: stories, loading } = useTranslateItems(
    rawStories,
    language,
    ["judul", "deskripsi"]  // ← field dari API yang ingin diterjemahkan
  );

  const maxIndex = Math.max(0, stories.length - visibleCount);
  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(maxIndex, c + 1));

  // Reset slider ke awal saat bahasa berubah
  useEffect(() => { setCurrent(0); }, [language]);

  return (
    <section ref={ref} className="bg-white py-6 pb-16 px-4 md:px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-[#1a4a35] px-8 md:px-16 lg:px-20 py-14 relative overflow-hidden">
          <div className="absolute bottom-0 right-0 font-serif text-[180px] font-bold leading-none select-none pointer-events-none opacity-[0.04] text-white">
            KASUS
          </div>

          {/* Header row */}
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4"
            style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease" }}
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-10 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                  {tx.caseLabel}
                </span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
                {tx.caseTitle}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/stories")}
                className="text-[#c9862a] font-bold text-sm flex items-center gap-2 border-b border-[#c9862a]/40 pb-0.5 hover:border-[#c9862a] transition-colors group/link"
              >
                {tx.viewAll}
                <HiArrowRight className="transition-transform duration-300 group-hover/link:translate-x-1" />
              </button>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={prev} disabled={current === 0}
                  className={`w-9 h-9 border flex items-center justify-center transition-all duration-200 ${current === 0 ? "border-white/15 text-white/15 cursor-not-allowed" : "border-[#c9862a] text-[#c9862a] hover:bg-[#c9862a] hover:text-white"}`}
                >
                  <HiArrowLeft size={14} />
                </button>
                <button
                  onClick={next} disabled={current >= maxIndex}
                  className={`w-9 h-9 border flex items-center justify-center transition-all duration-200 ${current >= maxIndex ? "border-white/15 text-white/15 cursor-not-allowed" : "border-[#c9862a] text-[#c9862a] hover:bg-[#c9862a] hover:text-white"}`}
                >
                  <HiArrowRight size={14} />
                </button>
                <span className="flex items-center text-white/30 text-xs font-bold tracking-widest ml-1">
                  {String(current + 1).padStart(2, "0")} / {String(maxIndex + 1).padStart(2, "0")}
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-white/10" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Loading skeleton */}
          {loading ? (
            <div className="flex gap-5">
              {rawStories.slice(0, visibleCount).map((_, i) => (
                <div
                  key={i}
                  className="bg-white overflow-hidden flex-shrink-0 animate-pulse"
                  style={{ width: `calc(${100 / visibleCount}% - ${(20 * (visibleCount - 1)) / visibleCount}px)` }}
                >
                  <div className="h-52 bg-[#e8e0d0]" />
                  <div className="p-6 flex flex-col gap-3">
                    <div className="h-3 bg-[#e8e0d0] rounded w-1/3" />
                    <div className="h-4 bg-[#e8e0d0] rounded w-3/4" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-full" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Cards viewport */}
              <div className="overflow-hidden">
                <div
                  className="flex gap-5 transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(calc(-${current} * (100% / ${visibleCount} + ${(20 * (visibleCount - 1)) / visibleCount}px)))` }}
                >
                  {stories.map((story, idx) => (
                    <div
                      key={idx}
                      className="bg-white overflow-hidden flex-shrink-0 flex flex-col group/card hover:shadow-xl transition-shadow duration-300"
                      style={{ width: `calc(${100 / visibleCount}% - ${(20 * (visibleCount - 1)) / visibleCount}px)` }}
                    >
                      <div className="h-52 overflow-hidden relative">
                        <img src={story.url} alt={story.judul} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-700" />
                        <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#c9862a] group-hover/card:w-full transition-all duration-500" />
                      </div>

                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <MdCalendarToday className="text-[#c9862a] text-xs" />
                          {/* Format tanggal juga ikut bahasa */}
                          <span className="text-[#c9862a] text-xs font-bold tracking-wide">
                            {formatTanggal(story.createdAt, language)}
                          </span>
                        </div>
                        {/* judul & deskripsi → sudah diterjemahkan oleh useTranslateItems */}
                        <h3 className="font-serif text-base font-bold text-[#1a4a35] leading-snug mb-3">
                          {story.judul}
                        </h3>
                        <div className="w-6 h-[2px] bg-[#c9862a]/40 mb-3 group-hover/card:w-12 transition-all duration-300" />
                        <p className="text-[#6b6456] text-sm leading-relaxed flex-1 line-clamp-3">
                          {truncateText(story.deskripsi)}
                        </p>
                        <div className="mt-6">
                          <button
                            onClick={() => navigate(`/view-stories/${story.id}`)}
                            className="flex items-center gap-2 text-[#1a4a35] font-bold text-sm border-b border-[#1a4a35]/30 pb-0.5 hover:border-[#c9862a] hover:text-[#c9862a] transition-all duration-200 group/btn"
                          >
                            {tx.learnMore}
                            <HiArrowRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                  <button
                    key={i} onClick={() => setCurrent(i)}
                    className={`transition-all duration-300 rounded-full ${i === current ? "w-8 h-2 bg-[#c9862a]" : "w-2 h-2 bg-white/20 hover:bg-white/50"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const ConsultingPage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <section id="hero"><Hero /></section>
    <section id="content"><ContentBlock /></section>
    <section id="case"><CaseStudies /></section>
    <section id="footer"><Footer /></section>
  </main>
);

export default ConsultingPage;